import React, { useState } from 'react'
import { useForm, FieldValues } from 'react-hook-form'
import { Navigate, useNavigate } from 'react-router'
import { Alert, Button, Input, Logo, useUserService } from '@climate/components'
import type { LoginResponse, LoginErrorBody } from '../types'
import { landAfterSignIn, useMfaHandover } from '../signIn'
import LogoImage from '../../assets/images/logo.png'

/**
 * The second step, for somebody who already has an authenticator.
 *
 * Reached only from the login screen, which hands over the token in router
 * state (see useMfaHandover). Arriving here any other way (a bookmark, a
 * reload, Back) means there is no token and no proven password, so it goes
 * back to the start rather than showing a form that could not possibly work.
 */
export default function MfaChallenge() {
  const navigate = useNavigate()
  const handover = useMfaHandover()
  const { register, handleSubmit, formState: { errors } } = useForm()
  const { verifyMfa, isVerifyingMfa } = useUserService(
    window.config.iam_service_url, window.config.session_cookie, 'iam'
  )
  const [remoteErrors, setRemoteErrors] = useState<string | undefined>()

  if (!handover?.mfaToken) return <Navigate to="/app/login" replace />

  const verifyAction = async (data: FieldValues) => {
    setRemoteErrors(undefined)

    try {
      const session = await verifyMfa({
        mfaToken: handover.mfaToken,
        code: String(data.code).trim(),
      }) as LoginResponse

      landAfterSignIn(session, handover.search, navigate)
    } catch (e: unknown) {
      const err = e as { data: { text: () => Promise<string> } }
      const { message, error }: LoginErrorBody = JSON.parse(await err.data.text())
      setRemoteErrors(message?.[0] || error)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-sm">
        <Logo LogoImage={LogoImage} alt="Tellus Iam logo"/>

        <h2 className="mt-6 text-center text-2xl font-bold text-gray-900">Two-step verification</h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Enter the 6-digit code from your authenticator app.
        </p>

        <div className="mt-8">
          <div className="bg-white py-8 px-4 shadow-sm sm:rounded-lg sm:px-10">
            <form id="verify_otp_form" className="space-y-6" onSubmit={handleSubmit(verifyAction)}>
              <div>
                <label htmlFor="code" className="pb-2 block text-sm text-gray-700">
                  Authentication code
                </label>
                <Input
                  id="code"
                  name="code"
                  // inputMode brings up the numeric keypad on a phone, where
                  // the code is being read from in the first place.
                  inputMode="numeric"
                  // The browser's own one-time-code hint, so iOS and Android
                  // offer the code rather than making it a copy-paste job.
                  autoComplete="one-time-code"
                  autoFocus
                  register={register('code', { required: 'Required' })}
                  errors={errors}
                />
              </div>

              {remoteErrors &&
                <Alert type="danger" message={remoteErrors}/>
              }

              <div className="py-2">
                <Button
                  id="verify_otp"
                  className="w-full justify-center"
                  type="submit"
                  size={3}
                  disabled={isVerifyingMfa}
                >
                  {isVerifyingMfa ? 'Verifying...' : 'Verify'}
                </Button>
              </div>
            </form>

            <p className="mt-6 text-center text-sm text-gray-500">
              Lost your device? Enter one of your recovery codes above instead. If you have none
              left, ask a Tellus administrator to reset it.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
